
import { defineComponent } from 'vue';
import { useStore } from 'vuex';
import { computed } from '@vue/runtime-core';
import { Actions, formFieldsStore, Getters, Mutations, userSettingsStore } from '@/store';

import Utils from '@/utils/QSUtils';
import ModalHeader from '@/modules/modals/components/ModalHeader.vue';
import ModalFooter from '@/modules/modals/components/ModalFooter.vue';
import ModalBody from '@/modules/modals/components/ModalBody.vue';
import ModalService from '@/modules/modals/services/modalService';
import SourceOrder from '@/modules/sort-order/SourceOrder.vue';
import FieldRules from '@/modules/field-rules/FieldRules.vue';
import PreferredCasing from '@/modules/casing/components/PreferredCasing.vue';
import FormsService from '@/services/2.6/FormsService';
import WebEnvConfigService from '@/services/TheWebEnvConfigService';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import GuideManager from '@/manager/GuideManager';
import LogUsageManager from '@/manager/LogUsageManager';
import OriginalValuesManager from '@/manager/2.6/OriginalValuesManager';
import IComp from '@/models/Comp';
import { SourceDataInitials } from '@/constants/SourceDataInitials';
import { newUpsertCompDataRequestByFieldId } from '@/store/2.6/models/IUpsertCompDataRequest';
import CompPrioritiesManager from '@/manager/2.6/CompPrioritiesManager';
import FormsManager from '@/manager/2.6/FormsManager';

export default defineComponent({
    components: { ModalHeader, ModalBody, ModalFooter, SourceOrder, PreferredCasing, FieldRules },
    props: {
        defaultTabIdx: {
            default: 0,
            required: false,
            type: Number,
        },
        hiddenTabIdx: {
            default: null,
            required: false,
            type: Array,
        },
        fieldId: {
            default: 0,
            required: true,
            type: Number,
        },
    },
    data() {
        return {
            modal: null,
            friendlyFieldName: '',
            fieldOriginalValue: '',
            fieldSourceInitial: '',
            sourceOrderCallback: {},
            fieldRulesCallback: [],
            fieldAwaitComplete: false,
            fieldRules: {},
            fieldSettings: {},
            activeComp: {} as IComp,
        };
    },
    setup() {
        const store = useStore();

        return {
            store,
        };
    },
    async beforeMount() {
        const webEnvConfig = await WebEnvConfigService.getInstance().getConfigByHost();

        const fieldData = await FormsManager.getFieldDataById(this.fieldId);
        this.friendlyFieldName = fieldData?.friendlyFieldName ?? '';

        const activeCompFieldData = computed(() => this.store.getters[Getters.CompAddresses.ActiveCompFieldData](fieldData?.fieldName));
        this.fieldSourceInitial = activeCompFieldData.value.sourceInitial;

        const activeComp = computed(() => this.store.getters[Getters.CompAddresses.ActiveComp]);
        this.activeComp = activeComp.value;
        const originalValue = OriginalValuesManager.getValueByAddressAndFieldId(this.activeComp.address, this.fieldId);
        this.fieldOriginalValue = originalValue ? originalValue.originalValue : activeCompFieldData.value.value;

        this.fieldRules = await new FormsService(webEnvConfig).getDataFieldRules(this.fieldId);
        this.fieldSettings = await new FormsService(webEnvConfig).getDataFieldSettings(this.fieldId);

        this.fieldAwaitComplete = true;
    },
    mounted() {
        const modalElement = this.$refs.dataPreferenceSettings;
        this.modal = ModalService.initialize(modalElement, this);
        ModalService.size(modalElement, 'xl');
        ModalService.open(this.modal);
    },
    computed: {
        SourceOrder: function () {
            const result = computed(() => userSettingsStore.DataSourceColumnOrder);
            return result.value;
        },
    },
    methods: {
        SaveDataPreferenceSettings: async function () {
            const sourceOrder: any = this.sourceOrderCallback;

            let fieldRules: any = this.fieldRulesCallback;

            const preferredCasing = document.querySelector('input[name="casing"]:checked') as HTMLInputElement;
            // @ts-expect-error: ...
            const originalPreferredCasingState = this.fieldSettings.preferredCasing;

            if (Utils.objectSize(sourceOrder) > 0) {
                await formFieldsStore.saveDataPreferenceSourceOrder({
                    fieldId: this.fieldId,
                    dataPreferenceSourceOrder: sourceOrder,
                    formCode: theLocalStorageUtils.get(LocalStorageKeys.formCode),
                });

                // ReRun Data Preference Settings and Translations for all Tabs on the field
                CompPrioritiesManager.runForAllCompsOnField(this.fieldId);
            }

            if (fieldRules.length > 0) {
                // Filter out all rules that are missing both textMatchCriteria and textEditValue
                const fieldRulesSave = fieldRules.filter((obj: { textMatchOption: number; textMatchCriteria: string; ruleStatus: string }) => (obj.textMatchOption === 3 && obj.textMatchCriteria === '' && obj.ruleStatus !== 'deleted') || (obj.textMatchCriteria !== '' && obj.ruleStatus !== 'deleted'));
                const fieldRulesLog = fieldRules.filter((obj: { textMatchCriteria: string; ruleStatus: string }) => obj.textMatchCriteria !== '');

                await formFieldsStore.saveFieldRules({
                    fieldId: this.fieldId,
                    fieldRules: fieldRulesSave,
                });

                if (fieldRulesLog.length > 0) {
                    for (var i = 0; i < fieldRulesLog.length; i++) {
                        if (fieldRulesLog[i]['ruleId'] === 0 && fieldRulesLog[i]['ruleStatus'] === 'added') {
                            LogUsageManager.createdTranslationRule(fieldRulesLog[i], this.fieldId);
                        } else if (fieldRulesLog[i]['ruleId'] > 0 && fieldRulesLog[i]['ruleStatus'] === 'updated') {
                            LogUsageManager.editedTranslationRule(fieldRulesLog[i], this.fieldId);
                        } else if (fieldRulesLog[i]['ruleId'] > 0 && fieldRulesLog[i]['ruleStatus'] === 'deleted') {
                            LogUsageManager.deletedTranslationRule(fieldRulesLog[i], this.fieldId);
                        }
                    }
                }

                // Add UT sourceInitial if rule is applied to a transfer field that does not have a sourceInitial.  ie. isBlank condition
                if (this.fieldSourceInitial === '') {
                    const request = newUpsertCompDataRequestByFieldId(this.activeComp as IComp, this.fieldId);
                    request.sourceInitial = SourceDataInitials.UserOverride;
                    request.description = 'DataPreferenceSettings.SaveDataPreferenceSettings()';
                    this.store.commit(Mutations.CompAddresses.UpsertCompSourceDataByFieldId, request);
                }

                // ReRun Rules for all Tabs
                this.store.dispatch(Actions.CompAddresses.TransferRunFieldRowTransferValueRulesOnAllCompsByFieldId, this.fieldId);
            }

            if (originalPreferredCasingState !== preferredCasing.value) {
                // await this.store.dispatch(Actions.FormFields.PreferredCasing, {
                //     fieldId: this.fieldId,
                //     preferredCasing: preferredCasing.value,
                // });

                await formFieldsStore.savePreferredCasing({
                    fieldId: this.fieldId,
                    preferredCasing: preferredCasing.value,
                });

                // ReRun Casing for all Tabs
                this.store.dispatch(Actions.CompAddresses.TransferRunFieldRowTransferValueCasingOnAllCompsByFieldId, this.fieldId);
                // this.store.dispatch(Actions.CompAddresses.TransferRunFieldRowTransferValueCasingOnAllUnVisitedCompsByFieldId, this.fieldId);
            }

            await Utils.sleep(500); // give it a bit to allow commits to resolve

            GuideManager.guideCompleted(5);
            ModalService.hide(this.modal);
        },
        isActiveTab(tabIdx: number) {
            return Number(this.defaultTabIdx) === tabIdx ? 'active' : '';
        },
        isActiveContent(tabIdx: number) {
            return Number(this.defaultTabIdx) === tabIdx ? 'active show' : '';
        },
        isHiddenTab(tabIdx: number) {
            return Utils.isValueInArray(this.hiddenTabIdx, tabIdx) ? true : false;
        },
        SourceOrderCallback(value: any) {
            this.sourceOrderCallback = value;
        },
        FieldRulesCallback(value: any) {
            this.fieldRulesCallback = value;
        },
    },
});
